import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, Icon } from '@ds/core';
import { Tooltip } from '@ds/feedback';
import { Checkbox } from '@ds/forms';
import { totalDeFiltros } from '@/data/comunicacao';
import type { GrupoDoFiltro, SelecaoDoFiltro } from '@/data/comunicacao';

export interface OpcaoDoFiltro {
    id: string;
    rotulo: string;
    nota?: string;
}

export interface GrupoDeOpcoes {
    id: GrupoDoFiltro;
    rotulo: string;
    icone: string;
    opcoes: OpcaoDoFiltro[];
}

export interface FiltroInteligenteProps {
    grupos: GrupoDeOpcoes[];
    selecao: SelecaoDoFiltro;
    onChange: (selecao: SelecaoDoFiltro) => void;
    /** Quantas conversas a seleção de agora deixa na lista. */
    resultado: number;
}

const LARGURA = 296;
const MIN_VISIVEL = 260;

function alternar(selecao: SelecaoDoFiltro, grupo: GrupoDoFiltro, id: string): SelecaoDoFiltro {
    const atual = selecao[grupo] ?? [];
    return { ...selecao, [grupo]: atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id] };
}

/**
 * Filtro inteligente — seleção múltipla por grupo.
 *
 * O painel vive num portal no `body`: a lista fica num contêiner com rolagem
 * própria, e popover dentro de contêiner recortado é popover invisível. É
 * ancorado sempre pelo TOPO, com a altura derivada do espaço real abaixo — o
 * protótipo aprendeu que alternar entre topo e base deixava o painel meio fora
 * da tela.
 */
export function FiltroInteligente({ grupos, selecao, onChange, resultado }: FiltroInteligenteProps) {
    const [aberto, setAberto] = useState(false);
    const botao = useRef<HTMLButtonElement>(null);
    const [posicao, setPosicao] = useState({ top: 0, left: 0, maxHeight: 'calc(100dvh - 24px)' });
    const total = totalDeFiltros(selecao);

    useLayoutEffect(() => {
        if (!aberto) return undefined;
        const calcular = () => {
            const r = botao.current?.getBoundingClientRect();
            if (!r) return;
            const top = Math.max(12, Math.min(Math.round(r.bottom + 6), window.innerHeight - MIN_VISIVEL - 12));
            setPosicao({
                top,
                left: Math.max(12, Math.min(Math.round(r.right - LARGURA), window.innerWidth - LARGURA - 12)),
                maxHeight: `calc(100dvh - ${top}px - 12px)`,
            });
        };
        calcular();
        window.addEventListener('resize', calcular);
        window.addEventListener('scroll', calcular, true);
        return () => {
            window.removeEventListener('resize', calcular);
            window.removeEventListener('scroll', calcular, true);
        };
    }, [aberto]);

    useEffect(() => {
        if (!aberto) return undefined;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setAberto(false);
                botao.current?.focus();
            }
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aberto]);

    const ativo = aberto || total > 0;

    return (
        <>
            <Tooltip label="Usar filtro inteligente">
                <button
                    ref={botao} type="button" aria-label="Usar filtro inteligente" aria-expanded={aberto} aria-haspopup="dialog"
                    onClick={() => setAberto((v) => !v)}
                    style={{
                        position: 'relative', width: 36, height: 36, flex: '0 0 auto', borderRadius: 'var(--radius-control)', cursor: 'pointer',
                        border: `1px solid ${ativo ? 'var(--border-brand)' : 'var(--border-strong)'}`,
                        background: ativo ? 'var(--surface-selected)' : 'var(--surface)',
                        color: ativo ? 'var(--text-brand)' : 'var(--neutral-600)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'var(--transition-control)',
                    }}
                >
                    <Icon name="sliders-horizontal" size={16} />
                    {total > 0 && (
                        <span
                            aria-hidden="true"
                            style={{
                                position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 'var(--radius-pill)',
                                background: 'var(--action-primary)', color: 'var(--text-inverse)', fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            {total}
                        </span>
                    )}
                </button>
            </Tooltip>

            {aberto && createPortal(
                <>
                    <button
                        type="button" aria-label="Fechar o filtro" tabIndex={-1} onClick={() => setAberto(false)}
                        style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'transparent', border: 0, cursor: 'default' }}
                    />
                    <div
                        role="dialog" aria-label="Filtro inteligente"
                        style={{
                            position: 'fixed', top: posicao.top, left: posicao.left, zIndex: 61, width: LARGURA, maxHeight: posicao.maxHeight, overflowY: 'auto',
                            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 12,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, borderBottom: '1px solid var(--border-subtle)' }}>
                            <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>Filtro inteligente</span>
                            {total > 0 && <Button size="sm" variant="ghost" onClick={() => onChange({})}>Limpar</Button>}
                        </div>

                        {grupos.filter((g) => g.opcoes.length > 0).map((g) => (
                            <fieldset key={g.id} style={{ margin: 0, padding: '11px 0', border: 0, borderBottom: '1px solid var(--border-subtle)' }}>
                                <legend style={{ padding: 0, marginBottom: 7, display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                    <Icon name={g.icone} size={13} />{g.rotulo}
                                </legend>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {g.opcoes.map((o) => (
                                        <Checkbox
                                            key={o.id}
                                            checked={(selecao[g.id] ?? []).includes(o.id)}
                                            onChange={() => onChange(alternar(selecao, g.id, o.id))}
                                            label={o.rotulo}
                                            description={o.nota}
                                        />
                                    ))}
                                </div>
                            </fieldset>
                        ))}

                        <div style={{ paddingTop: 11 }}>
                            <Button fullWidth onClick={() => setAberto(false)}>
                                Ver {resultado} {resultado === 1 ? 'conversa' : 'conversas'}
                            </Button>
                        </div>
                    </div>
                </>,
                document.body,
            )}
        </>
    );
}

/** O que está filtrado, à vista e removível sem abrir o painel. */
export function ChipsDoFiltro({ grupos, selecao, onChange }: Omit<FiltroInteligenteProps, 'resultado'>) {
    const chips = grupos.flatMap((g) => (selecao[g.id] ?? []).map((id) => ({
        grupo: g.id, id, rotulo: g.opcoes.find((o) => o.id === id)?.rotulo ?? id,
    })));
    if (!chips.length) return null;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {chips.map((c) => (
                <span
                    key={`${c.grupo}-${c.id}`}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 5px 0 9px', borderRadius: 'var(--radius-pill)',
                        background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 500, color: 'var(--text-body)',
                    }}
                >
                    {c.rotulo}
                    <button
                        type="button" aria-label={`Remover filtro ${c.rotulo}`} onClick={() => onChange(alternar(selecao, c.grupo, c.id))}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--neutral-500)', padding: 2, display: 'flex' }}
                    >
                        <Icon name="x" size={12} />
                    </button>
                </span>
            ))}
        </div>
    );
}
